import { Controller } from "@hotwired/stimulus"
import { CustomActionTextAttachmentNode } from "@37signals/lexxy"
import { $getSelection, $isRangeSelection, $insertNodes, $createTextNode, $getNearestNodeFromDOMNode, $getNodeByKey, $getRoot } from "lexical"

const LEXXY_VARIABLE_CONTENT_TYPE = "application/vnd.protectedshops.variable"

export default class extends Controller {
  static targets = ["dropzone"]

  static values = {
    titles: Array,
    variables: Array
  }

  connect() {
    this.restoreFromHeading(true)
    this.refreshLexxy()
    this.refreshHandler = this.refresh.bind(this)
    this.element.addEventListener("submit", this.handleSubmit, true)
    this.element.addEventListener("variable-insert:refresh", this.refreshHandler)
    this.frameRenderHandler = (event) => {
      const frame = event.target
      // Lexxy editors live in the layout content frame (not in observedFrameIds), so refresh on any frame load.
      this.refreshLexxy()
      if (frame && this.isObservedFrame(frame.id)) {
        setTimeout(() => this.restoreFromHeading(true), 0)
      }
    }
    document.addEventListener("turbo:frame-render", this.frameRenderHandler)
    document.addEventListener("turbo:frame-load", this.frameRenderHandler)
  }

  dropzoneTargetConnected(zone) {
    // Turbo Stream replace can swap markup without firing frame lifecycle events.
    this.restoreFromZone(zone, true)
    if (this.findLexxyEditor(zone)) {
      this.hydrateLexxyEditors()
    }
  }

  disconnect() {
    this.element.removeEventListener("submit", this.handleSubmit, true)
    this.element.removeEventListener("variable-insert:refresh", this.refreshHandler)
    if (this.frameRenderHandler) {
      document.removeEventListener("turbo:frame-render", this.frameRenderHandler)
      document.removeEventListener("turbo:frame-load", this.frameRenderHandler)
    }
  }

  refresh() {
    this.restoreFromHeading(true)
    this.refreshLexxy()
  }

  // Per-field key=>title map (only the field's own keys, so we don't ship every variable).
  localTitles(el) {
    const zone = el && el.closest ? el.closest("[data-variable-insert-target='dropzone']") : null
    const raw = zone && zone.dataset.variableInsertTitles
    if (!raw) {
      return null
    }
    try {
      return JSON.parse(raw)
    } catch (_error) {
      return null
    }
  }

  variableTitleForKey(key, titles = null) {
    if (titles) {
      const direct = titles[key] ?? titles[String(key).toLowerCase()]
      if (direct) {
        return direct
      }
    }
    const normalized = String(key).toLowerCase()
    const match = (this.variablesValue || []).find(
      (variable) => String(variable.key).toLowerCase() === normalized
    )
    return match ? match.title : key
  }

  // Displayed title of an existing chip, so reorder keeps the label without a global lookup.
  chipTitle(chipEl) {
    if (!chipEl) {
      return null
    }
    const clone = chipEl.cloneNode(true)
    clone.querySelectorAll("[data-variable-insert-role='token-remove'], .variable-pill__remove, button").forEach((el) => el.remove())
    const text = clone.textContent.trim()
    return text || null
  }

  handleSubmit = () => {
    this.element.querySelectorAll("[data-variable-insert-role='lexxy-editor']").forEach((editorEl) => {
      // An editor that hasn't loaded yet (slow connection, background tab)
      // reads as empty: copying it would overwrite the saved text in its field
      // with "" (independent QA). Its field still holds that text; keep it.
      if (!editorEl._variableInsertHydrated) return
      this.serializeLexxy(editorEl)
    })

    this.dropzoneTargets.forEach((zone) => {
      if (this.findEditable(zone)) {
        this.serializeEditable(zone)
      }
    })
  }

  dragStart(event) {
    const key = event.currentTarget.dataset.variableKey
    const title = event.currentTarget.dataset.variableTitle || ""
    if (!key) {
      return
    }

    event.dataTransfer.effectAllowed = "copy"
    event.dataTransfer.setData("text/plain", this.formatVariable(key))
    event.dataTransfer.setData("application/json", JSON.stringify({ key, title }))
    event.currentTarget.classList.add("dnd-dragging")
    this.element.classList.add("variable-drag-active")
  }

  dragEnd(event) {
    event.currentTarget.classList.remove("dnd-dragging")
    this.endDragState()
  }

  endDragState() {
    this.element.classList.remove("variable-drag-active")
    this.element
      .querySelectorAll(".dnd-over")
      .forEach((el) => el.classList.remove("dnd-over"))
  }

  // Resolve to the enclosing dropzone so the hover outline lands there, not on an inner control.
  dropzoneFor(target) {
    return (target instanceof Element && target.closest("[data-variable-insert-target='dropzone']")) || target
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    const zone = this.dropzoneFor(event.currentTarget)
    if (zone instanceof Element) {
      zone.classList.add("dnd-over")
      // Move the caret to follow the pointer so the drop position is visible.
      if (event.dataTransfer.types?.includes("application/json")) {
        const target = this.findLexxyEditor(zone) || this.findEditable(zone)
        if (target) {
          this.placeCaretAt(target, event.clientX, event.clientY)
        }
      }
    }
  }

  dragEnter(event) {
    event.preventDefault()
    const zone = this.dropzoneFor(event.currentTarget)
    if (zone instanceof Element) {
      zone.classList.add("dnd-over")
    }
  }

  dragLeave(event) {
    // Keep the highlight when moving onto the zone's own children (they also fire dragleave).
    const zone = this.dropzoneFor(event.currentTarget)
    if (zone instanceof Element && !zone.contains(event.relatedTarget)) {
      zone.classList.remove("dnd-over")
    }
  }

  drop(event) {
    if (event.variableInsertHandled) {
      return
    }

    // Only handle our variable drags (they carry a JSON payload).
    const payload = event.dataTransfer.getData("application/json")
    let data = null
    if (payload) {
      try {
        data = JSON.parse(payload)
      } catch (_error) {
        data = null
      }
    }
    if (!data || !data.key) {
      this.endDragState()
      return
    }

    event.variableInsertHandled = true
    event.preventDefault()
    event.stopPropagation()
    this.endDragState()

    const zone = event.currentTarget.closest("[data-variable-insert-target='dropzone']") || event.currentTarget
    const title = data.title || data.key

    const editable = (event.currentTarget.closest && event.currentTarget.closest("[data-variable-insert-role='editable']")) || this.findEditable(zone)
    if (editable) {
      if (data.move && this.movingToken && editable.contains(this.movingToken)) {
        this.movingToken.remove()
      }
      this.insertTokenAtPoint(editable, data.key, event.clientX, event.clientY, title)
      this.serializeEditable(zone)
      this.movingToken = null
      return
    }

    const lexxyEditor = (event.currentTarget.closest && event.currentTarget.closest("lexxy-editor")) || this.findLexxyEditor(zone)
    if (lexxyEditor) {
      this.insertLexxyVariable(lexxyEditor, data.key, title, event.clientX, event.clientY, data.move)
    }
  }

  // A chip clicked, or Enter/Space on it, goes in at the editor's caret (its end when it was never focused), so
  // placeholders don't need a mouse drag (QA).
  insertChip(event) {
    if (event.type === "keydown" && ![ "Enter", " " ].includes(event.key)) return
    event.preventDefault()
    const editorEl = this.element.querySelector("[data-variable-insert-role='lexxy-editor']")
    const editor = editorEl?.editor
    if (!editor) return

    const key = event.currentTarget.dataset.variableKey
    const title = event.currentTarget.dataset.variableTitle || key
    editor.update(() => {
      let selection = $getSelection()
      if (!$isRangeSelection(selection)) {
        $getRoot().selectEnd()
        selection = $getSelection()
      }
      selection.insertNodes([
        new CustomActionTextAttachmentNode({
          contentType: LEXXY_VARIABLE_CONTENT_TYPE,
          innerHtml: this.lexxyVariableInnerHtml(title, key),
          plainText: `<% ${key} %>`
        })
      ])
    })
    editorEl.focus?.()
  }

  formatVariable(key) {
    return `{{${key}}}`
  }

  restoreFromHeading(force = false) {
    this.restoreAllZones(force)
  }

  restoreAllZones(force = false) {
    this.dropzoneTargets.forEach((zone) => this.restoreFromZone(zone, force))
  }

  restoreFromZone(zone, force = false) {
    if (!force && zone.contains(document.activeElement)) {
      return
    }

    if (this.findEditable(zone)) {
      this.renderEditableFromHidden(zone)
    }
  }

  findHiddenInput(zone) {
    const inZone = zone.querySelector("[data-variable-insert-role='hidden-input']")
    if (inZone) {
      return inZone
    }

    const parent = zone.parentElement
    if (!parent) {
      return null
    }

    return parent.querySelector("[data-variable-insert-role='hidden-input']")
  }

  // ── Lexxy rich editor (inline variable chips) ──
  findLexxyEditor(zone) {
    return zone.querySelector("[data-variable-insert-role='lexxy-editor']")
  }

  onLexxyInitialize(event) {
    this.hydrateLexxyEditor(event.currentTarget)
    this.reinitLexxyPrompts(event.currentTarget)
  }

  // After Turbo nav a <lexxy-prompt> can connect before its editor; re-clone it now that the editor is ready so its @-trigger wires up.
  reinitLexxyPrompts(editorEl) {
    editorEl.querySelectorAll("lexxy-prompt").forEach((promptEl) => {
      promptEl.replaceWith(promptEl.cloneNode(true))
    })
  }

  onLexxyChange(event) {
    const editorEl = event.currentTarget
    // Ignore the change fired during initial load, before hydration.
    if (!editorEl._variableInsertHydrated) {
      return
    }
    this.serializeLexxy(editorEl)
  }

  // Reject dropped/pasted files when attachments are disabled (cancel lexxy:file-accept). Images go through
  // the server's limits (Backoffice::DirectUploadsController, #255), checked here first so the editor says
  // why instead of the editor's bare "Error uploading".
  onLexxyFileAccept(event) {
    const editor = event.currentTarget
    if (editor.hasAttribute("data-no-file-attachments")) {
      event.preventDefault()
    } else if (editor.hasAttribute("data-images-only")) {
      const file = event.detail?.file
      if (!file?.type?.startsWith("image/")) {
        event.preventDefault()
        return
      }
      // { "image/jpeg": ["jpg", "jpeg", "jpe", "jfif"], ... }: the type and the extension the server accepts.
      let types = {}
      try { types = JSON.parse(editor.dataset.imageTypes || "{}") } catch (_error) { types = {} }
      const extension = (file.name || "").split(".").pop().toLowerCase()
      const maxBytes = Number(editor.dataset.imageMaxBytes || 0)
      const typeRefused = Object.keys(types).length > 0 && !(types[file.type] || []).includes(extension)
      if (typeRefused || (maxBytes && file.size > maxBytes)) {
        event.preventDefault()
        window.boToast?.(editor.dataset.imageRefusedMessage, "alert")
      }
    }
  }

  hydrateLexxyEditors() {
    this.element.querySelectorAll("[data-variable-insert-role='lexxy-editor']").forEach((editorEl) => {
      if (!editorEl._variableInsertHydrated && editorEl.editor) {
        this.hydrateLexxyEditor(editorEl)
      }
    })
  }

  refreshLexxy() {
    this.hydrateLexxyEditors()
    // Delay lets a normally-initialising editor mount before the dead-state check.
    setTimeout(() => this.reviveDeadLexxyEditors(), 200)
  }

  // Turbo can restore a dead (reset) editor; trigger Lexxy's own reconnect via its `connected` attribute.
  reviveDeadLexxyEditors() {
    this.element.querySelectorAll("[data-variable-insert-role='lexxy-editor']").forEach((editorEl) => {
      if (editorEl.querySelector("[contenteditable]")) {
        return
      }
      editorEl._variableInsertHydrated = false
      editorEl._variableInsertDragListener = undefined
      if (editorEl.hasAttribute("connected")) {
        editorEl.removeAttribute("connected")
      }
    })
  }

  hydrateLexxyEditor(editorEl) {
    if (!editorEl || !editorEl.editor || editorEl._variableInsertHydrated) {
      return
    }
    const hidden = this.lexxyHiddenInput(editorEl)
    const raw = (hidden?.dataset.variableInsertRaw ?? hidden?.value ?? "").toString()
    if (raw.trim()) {
      editorEl.value = this.buildLexxyHtml(raw, this.localTitles(editorEl))
    }
    editorEl._variableInsertHydrated = true
    if (hidden) {
      delete hidden.dataset.variableInsertRaw
    }
    this.serializeLexxy(editorEl)

    // Keep chips draggable across Lexical re-renders.
    this.markLexxyChipsDraggable(editorEl)
    if (!editorEl._variableInsertDragListener) {
      editorEl._variableInsertDragListener = editorEl.editor.registerUpdateListener(
        () => this.markLexxyChipsDraggable(editorEl)
      )
    }
  }

  markLexxyChipsDraggable(editorEl) {
    const root = editorEl.querySelector("[contenteditable]")
    if (!root) {
      return
    }
    root.querySelectorAll(`action-text-attachment[content-type="${LEXXY_VARIABLE_CONTENT_TYPE}"]`).forEach((attachment) => {
      if (attachment.getAttribute("draggable") !== "true") {
        attachment.setAttribute("draggable", "true")
      }
    })
  }

  lexxyHiddenInput(editorEl) {
    const inputId = editorEl.dataset.variableInsertInput
    if (inputId) {
      const byId = this.element.querySelector(`#${CSS.escape(inputId)}`)
      if (byId) {
        return byId
      }
    }
    const zone = editorEl.closest("[data-variable-insert-target='dropzone']")
    return zone ? this.findHiddenInput(zone) : null
  }

  insertLexxyVariable(editorEl, key, title, x, y, move = false) {
    const editor = editorEl.editor
    if (!editor) {
      return
    }
    const movingAttachment = move && this.movingLexxy && this.movingLexxy.editorEl === editorEl
      ? this.movingLexxy.attachmentEl
      : null
    editorEl.focus?.()
    this.placeCaretInLexxy(editorEl, x, y)
    // Dropping onto a code block's non-editable chrome (gutter/line highlight)
    // leaves the caret outside the block, so the token lands outside it or as a
    // decorator node the code node silently discards. Force the caret to the end
    // of that code block so the plain-text token is inserted inside it.
    const codeEl = this.codeElementAtPoint(editorEl, x, y)
    if (codeEl && !this.caretInsideElement(codeEl)) {
      this.placeCaretAtEndOf(codeEl)
    }
    editor.update(() => {
      const selection = $getSelection()

      // A Lexical code block only accepts text-like children, so a decorator
      // (chip) node is silently dropped there. Detect a code-block ancestor and
      // insert the plain `<% key %>` token text instead — it serializes the same.
      let inCodeBlock = Boolean(codeEl)
      if (!inCodeBlock && $isRangeSelection(selection)) {
        let ancestor = selection.anchor.getNode()
        while (ancestor) {
          if (typeof ancestor.getType === "function" && ancestor.getType() === "code") {
            inCodeBlock = true
            break
          }
          ancestor = ancestor.getParent()
        }
      }

      // No space after the variable, as legacy's editor inserted none: the document prints the text as written, so a
      // space here before a comma came out as "GmbH , Anschrift" (QA).
      const nodes = inCodeBlock
        ? [$createTextNode(`<% ${key} %>`)]
        : [
            new CustomActionTextAttachmentNode({
              contentType: LEXXY_VARIABLE_CONTENT_TYPE,
              innerHtml: this.lexxyVariableInnerHtml(title, key),
              plainText: `<% ${key} %>`
            })
          ]

      if ($isRangeSelection(selection)) {
        selection.insertNodes(nodes)
      } else {
        $insertNodes(nodes)
      }
      // Remove the original after inserting, so the selection above stays valid.
      if (movingAttachment) {
        let original = $getNearestNodeFromDOMNode(movingAttachment)
        if (!original) {
          const movedKey = movingAttachment.getAttribute("data-lexical-node-key")
          if (movedKey) {
            original = $getNodeByKey(movedKey)
          }
        }
        if (original) {
          original.remove()
        }
      }
    })
    this.movingLexxy = null
    requestAnimationFrame(() => this.serializeLexxy(editorEl))
  }

  lexxyTokenDragStart(event) {
    const attachmentEl = event.target instanceof Element ? event.target.closest("action-text-attachment") : null
    const editorEl = attachmentEl ? attachmentEl.closest("lexxy-editor") : null
    const pill = attachmentEl ? attachmentEl.querySelector("[data-variable-key]") : null
    const key = pill ? pill.getAttribute("data-variable-key") : null
    if (!attachmentEl || !editorEl || !key) {
      return
    }
    this.movingLexxy = { editorEl, attachmentEl }
    event.dataTransfer.effectAllowed = "copy"
    event.dataTransfer.setData("application/json", JSON.stringify({ key, title: this.chipTitle(pill) || key, move: true }))
    event.dataTransfer.setData("text/plain", this.formatVariable(key))
    this.element.classList.add("variable-drag-active")
  }

  lexxyTokenDragEnd() {
    this.movingLexxy = null
    this.endDragState()
  }

  placeCaretInLexxy(editorEl, x, y) {
    const root = editorEl.querySelector('[contenteditable="true"]') || editorEl
    const range = this.caretRangeFromPoint(x, y)
    if (!range || !root.contains(range.startContainer)) {
      return false
    }
    const selection = window.getSelection()
    if (!selection) {
      return false
    }
    selection.removeAllRanges()
    selection.addRange(range)
    return true
  }

  // The <code>/<pre> block under the drop point within this editor, if any.
  codeElementAtPoint(editorEl, x, y) {
    const el = document.elementFromPoint ? document.elementFromPoint(x, y) : null
    const code = el && el.closest ? el.closest("code, pre") : null
    return code && editorEl.contains(code) ? code : null
  }

  caretInsideElement(el) {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return false
    }
    return el.contains(selection.getRangeAt(0).startContainer)
  }

  placeCaretAtEndOf(el) {
    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    const selection = window.getSelection()
    if (!selection) {
      return false
    }
    selection.removeAllRanges()
    selection.addRange(range)
    return true
  }

  serializeLexxy(editorEl) {
    const hidden = this.lexxyHiddenInput(editorEl)
    if (!hidden) {
      return
    }
    // An image-enabled editor (service setup instructions) holds plain rich text
    // with images and tables, no variables: kept as written. The token form below
    // turns paragraphs into line breaks and drops every non-variable attachment.
    hidden.value = editorEl.hasAttribute("data-images-only")
      ? (editorEl.value || "")
      : this.lexxyToTokens(editorEl.value || "")
  }

  // Keep identical to the <template type="editor"> in _lexxy_field.
  lexxyVariableInnerHtml(title, key) {
    return `<span class="variable-pill" data-variable-key="${this.escapeHtml(key)}" title="${this.escapeHtml(title)} (${this.escapeHtml(key)})">${this.escapeHtml(title)}<span class="variable-pill__remove" data-variable-insert-role="lexxy-token-remove" aria-hidden="true">×</span></span>`
  }

  onLexxyDeleteClick(event) {
    const remove = event.target instanceof Element
      ? event.target.closest("[data-variable-insert-role='lexxy-token-remove']")
      : null
    if (!remove) {
      return
    }
    const editorEl = remove.closest("lexxy-editor")
    const attachmentEl = remove.closest("action-text-attachment")
    if (!editorEl || !editorEl.editor || !attachmentEl) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    editorEl.editor.update(() => {
      let node = $getNearestNodeFromDOMNode(attachmentEl)
      if (!node) {
        const key = attachmentEl.getAttribute("data-lexical-node-key")
        if (key) {
          node = $getNodeByKey(key)
        }
      }
      if (node) {
        node.remove()
      }
    })
    requestAnimationFrame(() => this.serializeLexxy(editorEl))
  }

  buildLexxyHtml(raw, titles = null) {
    const withAttachments = (segment) =>
      String(segment).replace(/<%\s*([\w.-]+)\s*%>/g, (_match, key) => {
        const inner = this.lexxyVariableInnerHtml(this.variableTitleForKey(key, titles), key)
        return `<action-text-attachment content-type="${LEXXY_VARIABLE_CONTENT_TYPE}" content="${this.escapeHtml(inner)}"></action-text-attachment>`
      })

    // Wrap each line in its own <p>. Lexxy wraps bare top-level text nodes in
    // separate paragraphs on import, which would split "text + inline chip" and
    // push the chip onto its own line; keeping them inside one block keeps the
    // variable pill inline where it was dropped.
    return String(raw)
      .split(/<br\s*\/?>/i)
      .map((line) => `<p>${withAttachments(line) || "<br>"}</p>`)
      .join("")
  }

  // DOM-parse (not regex): the chip html lives in an escaped `content` attribute with literal angle brackets.
  lexxyToTokens(html) {
    const doc = new DOMParser().parseFromString(`<div id="vi-root">${html}</div>`, "text/html")
    const root = doc.getElementById("vi-root")
    if (!root) {
      return ""
    }
    const tokens = []
    const replaceWithToken = (node, key) => {
      const placeholder = `@@VITOKEN${tokens.length}@@`
      tokens.push(key ? `<% ${key} %>` : "")
      node.replaceWith(doc.createTextNode(placeholder))
    }
    root.querySelectorAll("action-text-attachment").forEach((att) => replaceWithToken(att, this.keyFromAttachmentEl(att)))
    root.querySelectorAll("[data-variable-key]").forEach((span) => replaceWithToken(span, span.getAttribute("data-variable-key")))

    // An empty line comes back as <p><br></p>; its own <br> must not add to the one the paragraph break becomes.
    let out = root.innerHTML
      .replace(/﻿/g, "")
      .replace(/<p([^>]*)>\s*<br[^>]*>\s*<\/p>/gi, "<p$1></p>")
      .replace(/<div><br><\/div>/gi, "<br>")
      .replace(/<\/p>\s*<p[^>]*>/gi, "<br>")
      .replace(/<\/?p[^>]*>/gi, "")
      .replace(/<div[^>]*>/gi, "")
      .replace(/<\/div>/gi, "<br>")
    out = out.replace(/@@VITOKEN(\d+)@@/g, (_match, index) => tokens[Number(index)] ?? "")
    return out.replace(/(<br>\s*)+$/i, "").trim()
  }

  keyFromAttachmentEl(att) {
    const direct = att.querySelector("[data-variable-key]")
    if (direct) {
      return direct.getAttribute("data-variable-key")
    }
    const content = att.getAttribute("content")
    if (!content) {
      return null
    }
    const span = new DOMParser().parseFromString(content, "text/html").querySelector("[data-variable-key]")
    return span ? span.getAttribute("data-variable-key") : null
  }

  // ── Inline token editor (contenteditable heading) ──
  findEditable(zone) {
    return zone.querySelector("[data-variable-insert-role='editable']")
  }

  renderEditableFromHidden(zone) {
    const editable = this.findEditable(zone)
    const hidden = this.findHiddenInput(zone)
    if (!editable || !hidden) {
      return
    }
    if (zone.contains(document.activeElement)) {
      return
    }
    editable.innerHTML = this.buildEditableHtml((hidden.value || "").trim(), this.localTitles(zone))
  }

  buildEditableHtml(raw, titles = null) {
    if (!raw) {
      return ""
    }
    const tokenRegex = /<%\s*([\w.-]+)\s*%>/g
    let html = ""
    let last = 0
    let match
    while ((match = tokenRegex.exec(raw)) !== null) {
      html += this.escapeHtml(raw.slice(last, match.index))
      html += this.editableTokenHtml(match[1], this.variableTitleForKey(match[1], titles))
      last = match.index + match[0].length
    }
    html += this.escapeHtml(raw.slice(last))
    return html
  }

  editableTokenHtml(key, title = null) {
    const label = title || this.variableTitleForKey(key)
    return `<span contenteditable="false" draggable="true" data-variable-insert-role="token" data-key="${this.escapeHtml(key)}" title="${this.escapeHtml(label)} (${this.escapeHtml(key)})" data-action="dragstart->variable-insert#tokenDragStart dragend->variable-insert#tokenDragEnd" class="mx-0.5 inline-flex h-5 select-none items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 align-middle text-xs text-gray-700 cursor-grab dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"><span>${this.escapeHtml(label)}</span><button type="button" data-variable-insert-role="token-remove" data-action="click->variable-insert#removeToken" class="cursor-pointer leading-none text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">&times;</button></span>`
  }

  serializeEditable(zone) {
    const editable = this.findEditable(zone)
    const hidden = this.findHiddenInput(zone)
    if (!editable || !hidden) {
      return
    }
    let out = ""
    editable.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        out += node.textContent
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.dataset && node.dataset.variableInsertRole === "token") {
          out += `<% ${node.dataset.key} %>`
        } else if (node.tagName === "BR") {
          out += " "
        } else {
          out += node.textContent || ""
        }
      }
    })
    hidden.value = out.replace(/ /g, " ").replace(/[ \t]+/g, " ").trim()
  }

  onEditableInput(event) {
    const zone = event.currentTarget.closest("[data-variable-insert-target='dropzone']")
    if (zone) {
      this.serializeEditable(zone)
    }
  }

  onEditableKeydown(event) {
    // Single-line heading: Enter must not add a line break.
    if (event.key === "Enter") {
      event.preventDefault()
    }
  }

  onEditablePaste(event) {
    event.preventDefault()
    const clipboard = event.clipboardData || window.clipboardData
    const text = clipboard ? clipboard.getData("text/plain") : ""
    if (text) {
      document.execCommand("insertText", false, text.replace(/[\r\n]+/g, " "))
    }
    const zone = event.currentTarget.closest("[data-variable-insert-target='dropzone']")
    if (zone) {
      this.serializeEditable(zone)
    }
  }

  insertTokenAtPoint(editable, key, x, y, title = null) {
    editable.focus()
    let range = this.caretRangeFromPoint(x, y)
    if (!range || !editable.contains(range.startContainer)) {
      range = document.createRange()
      range.selectNodeContents(editable)
      range.collapse(false)
    }
    const temp = document.createElement("div")
    temp.innerHTML = this.editableTokenHtml(key, title)
    const token = temp.firstChild
    range.insertNode(token)
    range.setStartAfter(token)
    range.collapse(true)
    const selection = window.getSelection()
    if (selection) {
      selection.removeAllRanges()
      selection.addRange(range)
    }
  }

  tokenDragStart(event) {
    const token = event.currentTarget
    this.movingToken = token
    const key = token.dataset.key || ""
    // effectAllowed must match dragOver's dropEffect ("copy") or the browser rejects the drop.
    event.dataTransfer.effectAllowed = "copy"
    event.dataTransfer.setData("application/json", JSON.stringify({ key, title: this.chipTitle(token) || key, move: true }))
    event.dataTransfer.setData("text/plain", this.formatVariable(key))
    token.classList.add("dnd-dragging")
    this.element.classList.add("variable-drag-active")
  }

  tokenDragEnd(event) {
    event.currentTarget.classList.remove("dnd-dragging")
    this.movingToken = null
    this.endDragState()
  }

  removeToken(event) {
    event.preventDefault()
    event.stopPropagation()
    const target = event.target instanceof Element ? event.target : null
    const token = target ? target.closest("[data-variable-insert-role='token']") : null
    if (!token) {
      return
    }
    const zone = token.closest("[data-variable-insert-target='dropzone']")
    token.remove()
    if (zone) {
      this.serializeEditable(zone)
    }
  }

  isObservedFrame(frameId) {
    return this.observedFrameIds().includes(frameId)
  }

  observedFrameIds() {
    return ["document_form", "document_section_detail", "document_part_form", "section_variation_form", "sub_section_form"]
  }

  // Place the caret at the point under (x, y) so a drop lands where the pointer is.
  placeCaretAt(editor, x, y) {
    const root = (editor && editor.editorElement) || editor
    if (!root) {
      return false
    }
    const range = this.caretRangeFromPoint(x, y)
    if (!range || !root.contains(range.startContainer)) {
      return false
    }
    const selection = window.getSelection()
    if (!selection) {
      return false
    }
    selection.removeAllRanges()
    selection.addRange(range)
    return true
  }

  caretRangeFromPoint(x, y) {
    if (document.caretRangeFromPoint) {
      return document.caretRangeFromPoint(x, y)
    }
    if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(x, y)
      if (pos) {
        const range = document.createRange()
        range.setStart(pos.offsetNode, pos.offset)
        range.collapse(true)
        return range
      }
    }
    return null
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

}
